/**
 * Review screen after choosing an AI design: roofs map + Production-style details.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CaretLeft, Check, CheckCircle, CircleNotch, CurrencyDollar, FloppyDisk, Info, Sparkle, WarningCircle, X } from '@phosphor-icons/react';
import { createPortal } from 'react-dom';
import {
    ACTIVE_LOSSES_VENDORS,
    type ActiveLossesVendor,
    normalizeLossesVendor,
    resolveVendorLossesKey,
    vendorLossesValues,
} from '../../Constants';
import { getPreContractModuleShadeColor, getPreContractShadeColor } from '../lib/preContractModuleLayout';
import {
    loadPreContractAiOptionsCache,
    parsePreContractTargetKwh,
    persistPreContractAiOptionsCache,
    type PreContractAiDesignOption,
} from '../lib/preContractAiDesigns';
import {
    collectIncludedModuleIds,
    postPciLossesVendorToEmbed,
    requestPciSimulateOptionsFromEmbed,
} from '../lib/preContractEmbedBridge';
import type { PreContractRoofModuleLayout } from '../lib/preContractTypes';
import {
    PRE_CONTRACT_DISABLED_PRODUCTS,
    PRE_CONTRACT_PRODUCTS,
    type PreContractProduct,
} from '../lib/preContractTypes';
import {
    resolveUtilityCompanyState,
    utilityCatalogEmptyMessage,
    utilityCompanyOptionsFromCatalog,
    utilityCompanySelectionPatch,
} from '../lib/preContractUtilityPrefill';
import { usePreContractUtilityCatalog } from './usePreContractUtilityCatalog';
import type { PreContractSelectedDesignStepProps } from './PreContractSelectedDesignStep.types';

export type { PreContractSelectedDesignStepProps };

function formatKwDc(value: number): string {
    if (!Number.isFinite(value) || value <= 0) return '—';
    return `${value.toFixed(3)} kW DC`;
}

function formatPlainNumber(value: number | null | undefined, digits = 0): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    if (digits <= 0) return String(Math.round(value));
    return value.toFixed(digits);
}

function formatOffsetPercent(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `${Math.round(value)}%`;
}

function averageIncludedMultiplier(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): number | null {
    let sum = 0;
    let count = 0;
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            const multiplier = Number(cell.multiplier);
            if (!Number.isFinite(multiplier) || multiplier <= 0) return;
            sum += multiplier;
            count += 1;
        });
    });
    if (count === 0) return null;
    return Math.round((sum / count) * 1000) / 1000;
}

function resolveSelectedOption(): PreContractAiDesignOption | null {
    const cached = loadPreContractAiOptionsCache();
    if (!cached) return null;
    const tab = cached.selectedTab === 'trees' && cached.treeCutOptions.length > 0 ? 'trees' : 'layouts';
    const pool = tab === 'trees' ? cached.treeCutOptions : cached.layoutOptions;
    return pool.find((item) => item.id === cached.selectedId) ?? pool[0] ?? null;
}

function estimateUtilityKwhFromLayouts(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): number | null {
    let sum = 0;
    let count = 0;
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            const production = Number(cell.productionKwh);
            const sa = Number(cell.solarAccess);
            if (!Number.isFinite(production) || production <= 0) return;
            if (Number.isFinite(sa) && sa > 0 && sa <= 100) {
                sum += production / (sa / 100);
            } else {
                sum += production;
            }
            count += 1;
        });
    });
    if (count === 0 || sum <= 0) return null;
    return Math.round(sum);
}

function buildDesignDetailRows(
    option: PreContractAiDesignOption | null,
    annualUsageKwh: number,
    lossesVendor: ActiveLossesVendor,
): {
    hero: {
        actualKwhLabel: string;
        actualOffset: number | null;
        systemKwLabel: string;
        moduleCountLabel: string;
    };
    rows: Array<{ label: string; value: string; tone?: 'muted' | 'accent' }>;
} {
    const multiplier =
        option?.multiplier != null && option.multiplier > 0
            ? option.multiplier
            : option
              ? averageIncludedMultiplier(option.layouts, option.roofSelections)
              : null;
    const actualKwh = option?.annualKwh ?? 0;
    const utilityKwh =
        option?.utilityKwh != null && option.utilityKwh > 0
            ? option.utilityKwh
            : option
              ? estimateUtilityKwhFromLayouts(option.layouts, option.roofSelections)
              : null;
    const efficiencyTsrf =
        option?.efficiencyTsrf != null && option.efficiencyTsrf > 0
            ? option.efficiencyTsrf
            : null;
    const utilityOffset =
        annualUsageKwh > 0 && utilityKwh != null && utilityKwh > 0
            ? Math.round((utilityKwh / annualUsageKwh) * 100)
            : null;
    const actualOffset =
        option?.offsetPercent != null
            ? option.offsetPercent
            : annualUsageKwh > 0 && actualKwh > 0
              ? Math.round((actualKwh / annualUsageKwh) * 100)
              : null;

    const rows: Array<{ label: string; value: string; tone?: 'muted' | 'accent' }> = [
        { label: 'System Size', value: option ? formatKwDc(option.systemKw) : '—' },
        {
            label: 'Modules',
            value: option && option.moduleCount > 0 ? String(option.moduleCount) : '—',
        },
        {
            label: 'Efficiency (TSRF)',
            value: formatPlainNumber(efficiencyTsrf),
        },
        {
            label: 'Multiplier',
            value: formatPlainNumber(multiplier, 3),
        },
        { label: 'Losses Vendor', value: option ? lossesVendor : '—', tone: 'accent' },
        {
            label: 'Solar Access',
            value:
                option && option.averageSolarAccess > 0
                    ? formatPlainNumber(option.averageSolarAccess)
                    : '—',
        },
        {
            label: 'kWh - Utility',
            value:
                utilityKwh != null && utilityKwh > 0
                    ? Math.round(utilityKwh).toLocaleString()
                    : '—',
        },
        {
            label: 'kWh - Actual',
            value: actualKwh > 0 ? Math.round(actualKwh).toLocaleString() : '—',
            tone: 'accent',
        },
        { label: 'Utility Offset', value: formatOffsetPercent(utilityOffset) },
        { label: 'Actual Offset', value: formatOffsetPercent(actualOffset), tone: 'accent' },
        {
            label: 'Annual Usage',
            value: annualUsageKwh > 0 ? Math.round(annualUsageKwh).toLocaleString() : '—',
        },
    ];

    return {
        hero: {
            actualKwhLabel: actualKwh > 0 ? Math.round(actualKwh).toLocaleString() : '—',
            actualOffset,
            systemKwLabel: option ? formatKwDc(option.systemKw) : '—',
            moduleCountLabel:
                option && option.moduleCount > 0 ? String(option.moduleCount) : '—',
        },
        rows,
    };
}

export const PreContractSelectedDesignStep: React.FC<PreContractSelectedDesignStepProps> = ({
    form,
    patchForm,
    onBack,
    onOpenPricing,
    onOpenOneDraw,
    onFinalize,
    onSaveOneButton,
    actionsDisabled = false,
    embedded = false,
}) => {
    const [openingOneDraw, setOpeningOneDraw] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [savingOneButton, setSavingOneButton] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ ok: boolean; text: string } | null>(null);
    const [lossesVendor, setLossesVendor] = useState<ActiveLossesVendor>(() =>
        normalizeLossesVendor('oneDRAW'),
    );
    const [lossesDetailsOpen, setLossesDetailsOpen] = useState(false);
    const [selected, setSelected] = useState<PreContractAiDesignOption | null>(() =>
        resolveSelectedOption(),
    );
    const [metricsLoading, setMetricsLoading] = useState(false);

    useEffect(() => {
        if (!selected) return undefined;
        const needsLive =
            !(selected.efficiencyTsrf != null && selected.efficiencyTsrf > 0) ||
            !(selected.utilityKwh != null && selected.utilityKwh > 0);
        if (!needsLive) return undefined;

        let cancelled = false;
        setMetricsLoading(true);
        void (async () => {
            try {
                const includedModuleIds = collectIncludedModuleIds(selected.layouts);
                if (includedModuleIds.length === 0) return;
                const fills =
                    selected.fillOrientation && selected.modulesLayout?.length
                        ? { [selected.fillOrientation]: selected.modulesLayout }
                        : undefined;
                const results = await requestPciSimulateOptionsFromEmbed(
                    [
                        {
                            id: selected.id,
                            includedModuleIds,
                            fillOrientation: selected.fillOrientation,
                        },
                    ],
                    fills,
                    selected.ghostedTreeIds,
                );
                if (cancelled || !results?.[0]) return;
                const sim = results[0];
                const next: PreContractAiDesignOption = {
                    ...selected,
                    annualKwh: sim.annualKwh > 0 ? sim.annualKwh : selected.annualKwh,
                    utilityKwh:
                        sim.utilityKwh > 0 ? sim.utilityKwh : selected.utilityKwh ?? null,
                    efficiencyTsrf:
                        sim.efficiency != null && sim.efficiency > 0
                            ? sim.efficiency
                            : selected.efficiencyTsrf ?? null,
                    multiplier:
                        sim.multiplier != null && sim.multiplier > 0
                            ? sim.multiplier
                            : selected.multiplier ?? null,
                    systemKw: sim.systemKw > 0 ? sim.systemKw : selected.systemKw,
                    averageSolarAccess:
                        sim.averageSolarAccess > 0
                            ? sim.averageSolarAccess
                            : selected.averageSolarAccess,
                    moduleCount: sim.moduleCount > 0 ? sim.moduleCount : selected.moduleCount,
                    modulesLayout: sim.modulesLayout?.length
                        ? sim.modulesLayout
                        : selected.modulesLayout,
                };
                setSelected(next);
                const cached = loadPreContractAiOptionsCache();
                if (cached) {
                    const patchPool = (pool: PreContractAiDesignOption[]) =>
                        pool.map((item) => (item.id === next.id ? { ...item, ...next } : item));
                    persistPreContractAiOptionsCache(
                        patchPool(cached.layoutOptions),
                        patchPool(cached.treeCutOptions),
                        cached.selectedId,
                        cached.selectedTab ?? 'layouts',
                    );
                }
            } finally {
                if (!cancelled) setMetricsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // Refresh once per selected design id.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected?.id]);

    const annualUsageKwh = parsePreContractTargetKwh(form.annualUsageKwh);
    const detailRows = useMemo(
        () => buildDesignDetailRows(selected, annualUsageKwh, lossesVendor),
        [selected, annualUsageKwh, lossesVendor],
    );
    const offsetTone =
        detailRows.hero.actualOffset == null
            ? 'none'
            : detailRows.hero.actualOffset >= 100
              ? 'over'
              : detailRows.hero.actualOffset >= 92
                ? 'near'
                : 'under';
    const selectedRoofs = useMemo(() => {
        if (!selected) return [];
        return [...selected.layouts]
            .sort((a, b) => a.roofIndex - b.roofIndex)
            .filter((layout) => layout.modules.some(Boolean));
    }, [selected]);
    const lossRows = useMemo(() => {
        const key = resolveVendorLossesKey(lossesVendor);
        return vendorLossesValues[key] ?? [];
    }, [lossesVendor]);

    const actionsReady =
        !actionsDisabled && !openingOneDraw && !finalizing && !savingOneButton && selected != null;
    const solarSelected = Boolean(form.products.Solar);

    const handleLossesVendor = (vendor: ActiveLossesVendor) => {
        if (vendor === lossesVendor) return;
        setLossesVendor(vendor);
        postPciLossesVendorToEmbed(vendor);
    };

    const handleAnnualUsageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        patchForm({ annualUsageKwh: event.target.value });
    };

    const handleUtilityRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        patchForm({ utilityRate: event.target.value });
    };

    const handleTargetMinPpaRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        patchForm({ targetMinPpaRate: event.target.value });
    };

    const handleOpenOneDraw = async () => {
        if (!selected || openingOneDraw || finalizing || savingOneButton) return;
        postPciLossesVendorToEmbed(lossesVendor);
        setOpeningOneDraw(true);
        try {
            await onOpenOneDraw(selected);
        } finally {
            setOpeningOneDraw(false);
        }
    };

    const handleFinalize = async () => {
        if (!solarSelected || !selected || openingOneDraw || finalizing || savingOneButton) return;
        postPciLossesVendorToEmbed(lossesVendor);
        setFinalizing(true);
        try {
            await onFinalize(selected);
        } finally {
            setFinalizing(false);
        }
    };

    const handleSaveOneButton = async () => {
        if (openingOneDraw || finalizing || savingOneButton) return;
        setSaveMessage(null);
        setSavingOneButton(true);
        try {
            const result = await onSaveOneButton();
            setSaveMessage({ ok: result.ok, text: result.message });
        } catch (error) {
            setSaveMessage({
                ok: false,
                text:
                    error instanceof Error && error.message.trim()
                        ? error.message
                        : 'Failed to save to oneBUTTON.',
            });
        } finally {
            setSavingOneButton(false);
        }
    };

    const toggleProduct = (product: PreContractProduct) => {
        if (PRE_CONTRACT_DISABLED_PRODUCTS.has(product)) return;
        patchForm({
            products: {
                ...form.products,
                [product]: !form.products[product],
            },
        });
    };

    const { catalog: utilityCatalog, status: utilityCatalogStatus } =
        usePreContractUtilityCatalog(resolveUtilityCompanyState(form, null));
    const utilityOptions = utilityCompanyOptionsFromCatalog(
        utilityCatalog,
        utilityCatalogStatus,
        form.utilityCompany,
    );

    const titleLabel = selected
        ? selected.roofLabels.length > 0
            ? selected.roofLabels.join(' · ')
            : selected.title
        : 'No design selected';

    return (
        <div
            id="pci-section-selected"
            className={`pci-selected-design${
                embedded ? ' pci-selected-design--embedded' : ''
            }`}
        >
            {!selected ? (
                <div className="pci-ai-designs__empty" role="status">
                    <WarningCircle size={20} weight="fill" />
                    <p>Select a design on the previous step to continue.</p>
                </div>
            ) : embedded ? (
                <section
                    className="pci-selected-design__panel pci-selected-design__panel--embedded-actions"
                    aria-label="Finalize design"
                >
                    <div className="pci-selected-design__finalize">
                        {saveMessage ? (
                            <p
                                className={`pci-selected-design__save-msg${
                                    saveMessage.ok
                                        ? ' pci-selected-design__save-msg--ok'
                                        : ' pci-selected-design__save-msg--err'
                                }`}
                                role="status"
                            >
                                {saveMessage.ok ? (
                                    <CheckCircle size={14} weight="fill" aria-hidden />
                                ) : (
                                    <WarningCircle size={14} weight="fill" aria-hidden />
                                )}
                                {saveMessage.text}
                            </p>
                        ) : (
                            <span className="pci-selected-design__finalize-spacer" aria-hidden />
                        )}
                        <button
                            type="button"
                            className="pci-btn pci-btn--secondary pci-selected-design__finalize-btn"
                            disabled={actionsDisabled || openingOneDraw || finalizing || savingOneButton}
                            onClick={handleSaveOneButton}
                        >
                            {savingOneButton ? (
                                <CircleNotch
                                    className="pci-ai-designs__spinner"
                                    size={18}
                                    weight="bold"
                                    aria-hidden
                                />
                            ) : (
                                <FloppyDisk size={18} weight="bold" aria-hidden />
                            )}
                            {savingOneButton ? 'Saving…' : 'Save oneBUTTON'}
                        </button>
                        {solarSelected ? (
                            <button
                                type="button"
                                className="pci-btn pci-btn--primary pci-selected-design__finalize-btn"
                                disabled={!actionsReady}
                                onClick={handleFinalize}
                                title="Finalize the solar design in oneDRAW"
                            >
                                {finalizing ? (
                                    <CircleNotch
                                        className="pci-ai-designs__spinner"
                                        size={18}
                                        weight="bold"
                                        aria-hidden
                                    />
                                ) : (
                                    <FloppyDisk size={18} weight="bold" aria-hidden />
                                )}
                                {finalizing ? 'Opening…' : 'Finalize design'}
                            </button>
                        ) : null}
                    </div>
                </section>
            ) : (
                <>
                <section className="pci-selected-design__panel" aria-label="Selected design summary">
                    <header className="pci-selected-design__panel-head">
                        <div className="pci-selected-design__panel-titles">
                            <p className="pci-board-v2-label">Selected design</p>
                            <h2 className="pci-selected-design__panel-title">{titleLabel}</h2>
                            <p className="pci-selected-design__panel-meta">
                                {selected.moduleCount} modules ·{' '}
                                {selected.roofLabels.length || selectedRoofs.length} roofs
                                {metricsLoading ? (
                                    <span className="pci-selected-design__metrics-loading">
                                        <CircleNotch
                                            className="pci-ai-designs__spinner"
                                            size={12}
                                            weight="bold"
                                            aria-hidden
                                        />
                                        Measuring…
                                    </span>
                                ) : null}
                            </p>
                        </div>
                        <div className="pci-selected-design__hero">
                            <div className="pci-selected-design__hero-main">
                                <span className="pci-selected-design__hero-label">kWh Actual</span>
                                <strong className="pci-selected-design__hero-value">
                                    {detailRows.hero.actualKwhLabel}
                                </strong>
                                <span className="pci-selected-design__hero-meta">
                                    {detailRows.hero.systemKwLabel} · {detailRows.hero.moduleCountLabel}{' '}
                                    modules
                                </span>
                            </div>
                            {detailRows.hero.actualOffset != null ? (
                                <div
                                    className={`pci-selected-design__hero-offset pci-selected-design__hero-offset--${offsetTone}`}
                                    aria-label={`${detailRows.hero.actualOffset}% actual offset`}
                                >
                                    <svg
                                        className="pci-selected-design__hero-ring"
                                        viewBox="0 0 36 36"
                                        aria-hidden
                                    >
                                        <circle
                                            className="pci-selected-design__hero-track"
                                            cx="18"
                                            cy="18"
                                            r="15.5"
                                        />
                                        <circle
                                            className="pci-selected-design__hero-fill"
                                            cx="18"
                                            cy="18"
                                            r="15.5"
                                            pathLength={100}
                                            strokeDasharray={`${Math.min(
                                                100,
                                                Math.max(0, detailRows.hero.actualOffset),
                                            )} 100`}
                                        />
                                    </svg>
                                    <span className="pci-selected-design__hero-offset-value">
                                        {detailRows.hero.actualOffset}%
                                    </span>
                                    <em>offset</em>
                                </div>
                            ) : null}
                        </div>
                    </header>

                    <div className="pci-selected-design__body">
                        <div className="pci-selected-design__roofs">
                            <div className="pci-selected-design__map">
                                {selectedRoofs.map((layout) => (
                                    <SelectedRoofPreview
                                        key={`selected-${layout.roofIndex}-${layout.label}`}
                                        layout={layout}
                                        unused={selected.roofSelections[layout.roofIndex] === false}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="pci-selected-design__details">
                            <ul className="pci-selected-design__details-list">
                                {detailRows.rows
                                    .filter((row) => row.label !== 'Losses Vendor')
                                    .map((row) => (
                                    <li
                                        key={row.label}
                                        className={
                                            row.tone === 'accent'
                                                ? 'pci-selected-design__details-row--accent'
                                                : undefined
                                        }
                                    >
                                        <span className="pci-selected-design__details-label">
                                            {row.label}
                                        </span>
                                        <span
                                            className={`pci-selected-design__details-value${
                                                row.value === '—'
                                                    ? ' pci-selected-design__details-value--empty'
                                                    : ''
                                            }`}
                                        >
                                            {row.value}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <section className="pci-selected-design__context" aria-label="Products and utility">
                        <div className="pci-selected-design__context-block">
                            <p className="pci-board-v2-label">Products</p>
                            <div className="pci-product-chips" role="group" aria-label="Products">
                                {PRE_CONTRACT_PRODUCTS.map((product) => {
                                    const selectedProduct = form.products[product];
                                    const disabled = PRE_CONTRACT_DISABLED_PRODUCTS.has(product);
                                    return (
                                        <button
                                            key={product}
                                            type="button"
                                            className={`pci-product-chip${
                                                selectedProduct ? ' pci-product-chip--on' : ''
                                            }${disabled ? ' pci-product-chip--disabled' : ''}`}
                                            aria-pressed={selectedProduct}
                                            aria-disabled={disabled}
                                            disabled={disabled}
                                            title={disabled ? 'Coming soon' : undefined}
                                            onClick={() => toggleProduct(product)}
                                        >
                                            <span className="pci-product-chip__name">
                                                {product === 'RR'
                                                    ? 'Roof + Solar'
                                                    : product === 'RRR'
                                                      ? 'Roof + Solar + Battery'
                                                      : product}
                                            </span>
                                            {selectedProduct ? (
                                                <Check
                                                    className="pci-product-chip__check"
                                                    size={12}
                                                    weight="bold"
                                                />
                                            ) : null}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="pci-selected-design__context-block">
                            <p className="pci-board-v2-label">Utility details</p>
                            <div className="pci-selected-design__utility">
                                <label className="pci-field pci-field--full">
                                    <span>Utility company</span>
                                    <select
                                        value={form.utilityCompany}
                                        disabled={utilityCatalogStatus === 'loading'}
                                        onChange={(e) =>
                                            patchForm(
                                                utilityCompanySelectionPatch(
                                                    e.target.value,
                                                    utilityCatalog,
                                                ),
                                            )
                                        }
                                    >
                                        <option value="">
                                            {utilityCatalogStatus === 'loading'
                                                ? 'Loading utility providers…'
                                                : 'Select utility provider'}
                                        </option>
                                        {utilityOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {utilityCatalogStatus === 'empty' ? (
                                    <p
                                        className="pci-opp-prefill pci-opp-prefill--compact pci-opp-prefill--warn pci-field--full"
                                        role="status"
                                    >
                                        {utilityCatalogEmptyMessage(form.installState)}
                                    </p>
                                ) : null}
                                <label className="pci-field">
                                    <span>Annual usage (kWh)</span>
                                    <input
                                        type="number"
                                        value={form.annualUsageKwh}
                                        onChange={handleAnnualUsageChange}
                                    />
                                </label>
                                <label className="pci-field">
                                    <span>All-in rate ($/kWh)</span>
                                    <input
                                        type="number"
                                        step="0.001"
                                        value={form.utilityRate}
                                        onChange={handleUtilityRateChange}
                                    />
                                </label>
                                <label className="pci-field pci-field--full">
                                    <span>Target/Min PPA Rate ($/kWh)</span>
                                    <input
                                        type="number"
                                        step="0.001"
                                        min="0"
                                        value={form.targetMinPpaRate}
                                        onChange={handleTargetMinPpaRateChange}
                                    />
                                </label>
                            </div>
                        </div>
                    </section>

                    <footer className="pci-selected-design__losses" aria-label="Losses">
                        <span className="pci-selected-design__losses-label">Losses</span>
                        <div className="pci-selected-design__losses-switch" role="group" aria-label="Losses vendor">
                            {ACTIVE_LOSSES_VENDORS.map((item) => (
                                <button
                                    key={item.value}
                                    type="button"
                                    className={`pci-selected-design__losses-btn${
                                        lossesVendor === item.value
                                            ? ' pci-selected-design__losses-btn--on'
                                            : ''
                                    }`}
                                    aria-pressed={lossesVendor === item.value}
                                    onClick={() => handleLossesVendor(item.value)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="pci-selected-design__losses-info"
                            aria-haspopup="dialog"
                            aria-expanded={lossesDetailsOpen}
                            title="View losses details"
                            onClick={() => setLossesDetailsOpen(true)}
                        >
                            <Info size={14} weight="bold" />
                            Details
                        </button>
                    </footer>

                    <div className="pci-selected-design__finalize">
                        {saveMessage ? (
                            <p
                                className={`pci-selected-design__save-msg${
                                    saveMessage.ok
                                        ? ' pci-selected-design__save-msg--ok'
                                        : ' pci-selected-design__save-msg--err'
                                }`}
                                role="status"
                            >
                                {saveMessage.ok ? (
                                    <CheckCircle size={14} weight="fill" aria-hidden />
                                ) : (
                                    <WarningCircle size={14} weight="fill" aria-hidden />
                                )}
                                {saveMessage.text}
                            </p>
                        ) : (
                            <span className="pci-selected-design__finalize-spacer" aria-hidden />
                        )}
                        <button
                            type="button"
                            className="pci-btn pci-btn--secondary pci-selected-design__finalize-btn"
                            disabled={actionsDisabled || openingOneDraw || finalizing || savingOneButton}
                            onClick={handleSaveOneButton}
                        >
                            {savingOneButton ? (
                                <CircleNotch
                                    className="pci-ai-designs__spinner"
                                    size={18}
                                    weight="bold"
                                    aria-hidden
                                />
                            ) : (
                                <FloppyDisk size={18} weight="bold" aria-hidden />
                            )}
                            {savingOneButton ? 'Saving…' : 'Save oneBUTTON'}
                        </button>
                        {solarSelected ? (
                            <button
                                type="button"
                                className="pci-btn pci-btn--primary pci-selected-design__finalize-btn"
                                disabled={!actionsReady}
                                onClick={handleFinalize}
                                title="Finalize the solar design in oneDRAW"
                            >
                                {finalizing ? (
                                    <CircleNotch
                                        className="pci-ai-designs__spinner"
                                        size={18}
                                        weight="bold"
                                        aria-hidden
                                    />
                                ) : (
                                    <FloppyDisk size={18} weight="bold" aria-hidden />
                                )}
                                {finalizing ? 'Opening…' : 'Finalize design'}
                            </button>
                        ) : null}
                    </div>
                </section>

                {lossesDetailsOpen
                    ? createPortal(
                          <LossesDetailsPopup
                              vendor={lossesVendor}
                              rows={lossRows}
                              onClose={() => setLossesDetailsOpen(false)}
                          />,
                          document.body,
                      )
                    : null}
                </>
            )}

            {!embedded ? (
            <div className="pci-board-fab-dock" aria-label="Board actions">
                <div className="pci-board-fab-dock__start">
                    <button
                        type="button"
                        className="pci-board-fab__btn"
                        onClick={onBack}
                        title="Back"
                        aria-label="Back"
                    >
                        <CaretLeft size={20} weight="bold" />
                    </button>
                </div>
                <div className="pci-board-fab-dock__main" aria-hidden />
                <div className="pci-board-fab-dock__end">
                    <button
                        type="button"
                        className="pci-board-fab__btn"
                        disabled={!actionsReady}
                        onClick={onOpenPricing}
                        title="Pricing"
                        aria-label="Pricing"
                    >
                        <CurrencyDollar size={20} weight="bold" />
                    </button>
                    <button
                        type="button"
                        className="pci-board-fab__btn pci-board-fab__btn--primary"
                        disabled={!actionsReady}
                        onClick={handleOpenOneDraw}
                        title={openingOneDraw ? 'Opening…' : 'Open in OneDraw'}
                        aria-label={openingOneDraw ? 'Opening OneDraw' : 'Open in OneDraw'}
                    >
                        {openingOneDraw ? (
                            <CircleNotch className="pci-ai-designs__spinner" size={20} weight="bold" />
                        ) : (
                            <Sparkle size={20} weight="fill" />
                        )}
                    </button>
                </div>
            </div>
            ) : null}
        </div>
    );
};

function LossesDetailsPopup({
    vendor,
    rows,
    onClose,
}: {
    vendor: ActiveLossesVendor;
    rows: Array<{ lossName: string; percentage: string }>;
    onClose: () => void;
}) {
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="pci-selected-design__losses-overlay" role="presentation" onClick={onClose}>
            <div
                ref={dialogRef}
                className="pci-selected-design__losses-dialog"
                role="dialog"
                aria-modal="true"
                aria-label={`${vendor} losses details`}
                onClick={(event) => event.stopPropagation()}
            >
                <header className="pci-selected-design__losses-dialog-head">
                    <div>
                        <p className="pci-board-v2-label">Losses</p>
                        <h3>{vendor} breakdown</h3>
                    </div>
                    <button
                        type="button"
                        className="pci-selected-design__losses-dialog-close"
                        aria-label="Close losses details"
                        onClick={onClose}
                    >
                        <X size={16} weight="bold" />
                    </button>
                </header>
                <table className="pci-selected-design__losses-table">
                    <thead>
                        <tr>
                            <th scope="col">Losses</th>
                            <th scope="col">%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.lossName}>
                                <td>{row.lossName}</td>
                                <td>{row.percentage}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SelectedModuleCell({
    cell,
}: {
    cell: PreContractRoofModuleLayout['modules'][number];
}) {
    if (!cell) {
        return <span className="pci-selected-mod pci-selected-mod--gap" />;
    }
    return (
        <span
            className={`pci-selected-mod${cell.included ? '' : ' pci-selected-mod--off'}`}
            style={{ backgroundColor: getPreContractModuleShadeColor(cell) }}
            title={cell.id}
        />
    );
}

function SelectedRoofPreview({
    layout,
    unused,
}: {
    layout: PreContractRoofModuleLayout;
    unused: boolean;
}) {
    const hasPresent = layout.modules.some(Boolean);
    if (!hasPresent) return null;
    const hasIncluded = layout.modules.some((cell) => cell?.included);
    const activeCount = layout.modules.filter((cell) => cell?.included).length;
    const totalCount = layout.modules.filter(Boolean).length;

    return (
        <div
            className={`pci-selected-roof pci-selected-roof--${layout.moduleOrientation ?? 'portrait'}${
                unused || !hasIncluded ? ' pci-selected-roof--unused' : ''
            }`}
            style={{ ['--pci-selected-roof-cols' as string]: String(layout.cols) }}
        >
            <div className="pci-selected-roof__head">
                <strong>{layout.label}</strong>
                <span>
                    {activeCount}/{totalCount}
                </span>
            </div>
            <div className="pci-selected-roof__grid">
                {layout.modules.map((cell, index) => (
                    <SelectedModuleCell
                        key={cell?.id ?? `gap-${layout.roofIndex}-${index}`}
                        cell={cell}
                    />
                ))}
            </div>
        </div>
    );
}

export default PreContractSelectedDesignStep;
